/**
 * Guardie "cambio codice CER con kg già a saldo" — tipi e parsing condivisi (#116).
 *
 * Il movimento di ingresso registra il materiale come snapshot del CER
 * dell'ordine: cambiarlo dopo lo stoccaggio lasciava i kg sotto il codice
 * vecchio. Il backend risponde ora con tre 409 STRUTTURATI, distinti dal campo
 * `code` (mai dal testo del messaggio):
 *
 *  - `conferma_propagazione_ledger_richiesta` → CONFERMABILE: su conferma si
 *    rinvia la stessa PATCH con `conferma_propagazione_ledger: true` e il
 *    backend sposta i kg con un movimento di rettifica;
 *  - `cer_bloccato_conferimenti_fatturati` e `cer_saldo_insufficiente` → NON
 *    confermabili: si mostrano e basta, nessun rilancio possibile.
 *
 * Gemello di `modificaFiscale.ts`, stessa disciplina: parser puri, `null` per
 * qualunque altro errore, nessuna dipendenza da axios.
 */

/** Riga del payload: un conferimento su una coppia (area, materiale). */
export interface ConferimentoCoinvolto {
  id: string;
  numero: string | null;
  codice_ordine: string | null;
  area_id: string;
  area_codice: string | null;
  materiale: string;
  kg: number;
  fatturato: boolean;
  /** Solo su `cer_saldo_insufficiente`. */
  saldo_area_kg?: number;
  mancanti_kg?: number;
}

/** 409 confermabile: i kg si possono spostare sul codice nuovo. */
export interface ConfermaPropagazioneDetail {
  code: 'conferma_propagazione_ledger_richiesta';
  cer_attuale: string | null;
  cer_nuovo: string | null;
  totale_kg: number;
  conferimenti: ConferimentoCoinvolto[];
}

/** 409 NON confermabili: nessun rilancio, serve intervento manuale. */
export interface CerBloccatoDetail {
  code: 'cer_bloccato_conferimenti_fatturati' | 'cer_saldo_insufficiente';
  conferimenti: ConferimentoCoinvolto[];
}

/** 409 della pagina impostazioni (via di massa): solo conteggi. */
export interface CerInUsoDetail {
  code: 'cer_in_uso_su_conferimenti_stoccati';
  ordini: number;
  conferimenti: number;
}

function detail409(err: unknown): Record<string, unknown> | null {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail;
  if (!detail || typeof detail !== 'object') return null;
  return detail as Record<string, unknown>;
}

function numero(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function conferimenti(v: unknown): ConferimentoCoinvolto[] {
  if (!Array.isArray(v)) return [];
  return v.map((raw) => {
    const c = (raw ?? {}) as Partial<ConferimentoCoinvolto>;
    return {
      id: String(c.id ?? ''),
      numero: c.numero ?? null,
      codice_ordine: c.codice_ordine ?? null,
      area_id: String(c.area_id ?? ''),
      area_codice: c.area_codice ?? null,
      materiale: String(c.materiale ?? ''),
      kg: numero(c.kg),
      fatturato: c.fatturato === true,
      // Presenti solo sul blocco per saldo insufficiente: restano undefined
      // altrove, così la modale sa quali colonne mostrare.
      saldo_area_kg: typeof c.saldo_area_kg === 'number' ? c.saldo_area_kg : undefined,
      mancanti_kg: typeof c.mancanti_kg === 'number' ? c.mancanti_kg : undefined,
    };
  });
}

/** Riconosce il 409 CONFERMABILE. Null per ogni altro errore. */
export function parseConfermaPropagazione(err: unknown): ConfermaPropagazioneDetail | null {
  const d = detail409(err);
  if (d?.code !== 'conferma_propagazione_ledger_richiesta') return null;
  return {
    code: 'conferma_propagazione_ledger_richiesta',
    cer_attuale: typeof d.cer_attuale === 'string' ? d.cer_attuale : null,
    cer_nuovo: typeof d.cer_nuovo === 'string' ? d.cer_nuovo : null,
    totale_kg: numero(d.totale_kg),
    conferimenti: conferimenti(d.conferimenti),
  };
}

/** Riconosce i due 409 NON confermabili. Null per ogni altro errore. */
export function parseCerBloccato(err: unknown): CerBloccatoDetail | null {
  const d = detail409(err);
  if (d?.code !== 'cer_bloccato_conferimenti_fatturati' && d?.code !== 'cer_saldo_insufficiente') {
    return null;
  }
  return { code: d.code, conferimenti: conferimenti(d.conferimenti) };
}

/** Riconosce il 409 di blocco della pagina impostazioni CER. */
export function parseCerInUso(err: unknown): CerInUsoDetail | null {
  const d = detail409(err);
  if (d?.code !== 'cer_in_uso_su_conferimenti_stoccati') return null;
  return {
    code: 'cer_in_uso_su_conferimenti_stoccati',
    ordini: numero(d.ordini),
    conferimenti: numero(d.conferimenti),
  };
}
